import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Select, Space, Switch, Table, Tag, message } from 'antd';
import { ReloadOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { adminAPI } from '../../services/api';
import { TeacherRoster, TeacherRosterCourse, TeacherRosterFilters, TeacherRosterStudent } from '../../types';
import './TeacherRosterPage.css';

const TeacherRosterPage: React.FC = () => {
  const [filters, setFilters] = useState<TeacherRosterFilters>({ teachers: [], semesters: [] });
  const [teacher, setTeacher] = useState<string>();
  const [semester, setSemester] = useState<string>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [roster, setRoster] = useState<TeacherRoster | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchRoster = useCallback(async (nextTeacher = teacher, nextSemester = semester, nextIncludeInactive = includeInactive) => {
    if (!nextTeacher) {
      return;
    }

    setLoading(true);
    try {
      const data = await adminAPI.getTeacherRoster({
        teacher: nextTeacher,
        semester: nextSemester,
        includeInactive: nextIncludeInactive,
      });
      setRoster(data);
    } catch (error) {
      message.error('获取老师名单失败');
    } finally {
      setLoading(false);
    }
  }, [includeInactive, semester, teacher]);

  const fetchFilters = useCallback(async () => {
    try {
      const data = await adminAPI.getTeacherRosterFilters();
      setFilters(data);
      if (!teacher && data.teachers.length > 0) {
        setTeacher(data.teachers[0]);
      }
    } catch (error) {
      message.error('获取老师列表失败');
    }
  }, [teacher]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (teacher) {
      fetchRoster(teacher, semester, includeInactive);
    }
  }, [fetchRoster, teacher, semester, includeInactive]);

  const filteredCourses = useMemo(() => {
    const text = keyword.trim();
    if (!roster || !text) {
      return roster?.courses || [];
    }

    return roster.courses
      .map((course) => ({
        ...course,
        students: course.students.filter((student) =>
          [student.realName, student.phone, student.grade]
            .filter(Boolean)
            .some((value) => String(value).includes(text))
        ),
      }))
      .filter((course) =>
        course.students.length > 0 ||
        [course.name, course.location, course.semester]
          .filter(Boolean)
          .some((value) => String(value).includes(text))
      );
  }, [keyword, roster]);

  const visibleStudentCount = filteredCourses.reduce((sum, course) => sum + course.students.length, 0);

  const studentColumns = [
    {
      title: '学生姓名',
      dataIndex: 'realName',
      key: 'realName',
      width: 140,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: '年级',
      dataIndex: 'grade',
      key: 'grade',
      width: 100,
      render: (grade: string) => grade || '-',
    },
  ];

  const renderCourseTitle = (course: TeacherRosterCourse) => (
    <div>
      <div className="teacher-roster-course-title">
        <span className="teacher-roster-course-name">{course.name}</span>
        <Tag color="blue">{course.semester || '未设置学期'}</Tag>
        <Tag color={course.status === 'active' ? 'green' : 'default'}>{course.status}</Tag>
        <Tag color={course.confirmedCount === course.currentStudents ? 'cyan' : 'orange'}>
          {course.confirmedCount}/{course.maxStudents}
        </Tag>
      </div>
      <div className="teacher-roster-course-subtitle">
        {course.location}
        {course.classTimeDescription ? ` · ${course.classTimeDescription}` : ''}
        {course.confirmedCount !== course.currentStudents
          ? ` · 系统人数 ${course.currentStudents}，名单人数 ${course.confirmedCount}`
          : ''}
      </div>
    </div>
  );

  return (
    <div className="teacher-roster-page">
      <div className="teacher-roster-header">
        <div>
          <h1 className="teacher-roster-title">老师学员名单</h1>
          <div className="teacher-roster-meta">
            实时读取当前报名数据，仅统计 confirmed 学员。
            {roster?.generatedAt ? ` 最后刷新：${new Date(roster.generatedAt).toLocaleString()}` : ''}
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchRoster()} loading={loading}>
          刷新
        </Button>
      </div>

      <div className="teacher-roster-filters">
        {filters.canChooseTeacher ? (
          <Select
            showSearch
            placeholder="选择老师"
            value={teacher}
            onChange={setTeacher}
            style={{ minWidth: 180 }}
            options={filters.teachers.map((item) => ({ label: item, value: item }))}
          />
        ) : (
          <Input value={teacher} readOnly style={{ maxWidth: 180 }} />
        )}
        <Select
          allowClear
          placeholder="全部学期"
          value={semester}
          onChange={setSemester}
          style={{ minWidth: 160 }}
          options={filters.semesters.map((item) => ({ label: item, value: item }))}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索学生、手机号、班级"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Space>
          <Switch checked={includeInactive} onChange={setIncludeInactive} />
          <span>包含非 active 课程</span>
        </Space>
      </div>

      <div className="teacher-roster-summary">
        <div className="teacher-roster-summary-item">
          <div className="teacher-roster-summary-label">老师</div>
          <div className="teacher-roster-summary-value">{roster?.teacher || '-'}</div>
        </div>
        <div className="teacher-roster-summary-item">
          <div className="teacher-roster-summary-label">课程数</div>
          <div className="teacher-roster-summary-value">{filteredCourses.length}</div>
        </div>
        <div className="teacher-roster-summary-item">
          <div className="teacher-roster-summary-label">学员数</div>
          <div className="teacher-roster-summary-value">{keyword ? visibleStudentCount : roster?.totalStudents || 0}</div>
        </div>
      </div>

      {filteredCourses.length === 0 ? (
        <Empty description="暂无名单数据" />
      ) : (
        filteredCourses.map((course) => (
          <Card
            key={course.id}
            className="teacher-roster-course"
            title={renderCourseTitle(course)}
            extra={<TeamOutlined />}
          >
            <Table<TeacherRosterStudent>
              rowKey="registrationId"
              columns={studentColumns}
              dataSource={course.students}
              pagination={false}
              size="middle"
              loading={loading}
            />
          </Card>
        ))
      )}
    </div>
  );
};

export default TeacherRosterPage;
